import React from "react";
import { Card, CardContent } from "src/components/ui/Card";
import { Badge } from "src/components/ui/Badge";
import { Modal } from "src/components/ui/Modal";
import { Button } from "src/components/ui/Button";
import { FormInput } from "src/components/ui/FormInput";
import { StudentExtendedProfileData } from "src/pages/student/Profile";
import SectionHeader from "./SectionHeader";
import { ChipInput, ChipOption } from "src/components/ui/ChipInput";
import { useQuery } from '@tanstack/react-query';
import { supabase } from 'src/lib/supabaseClient';
import { learningGoals as learningGoalsList } from 'src/constants/learningGoals';

interface GeneralInformationSectionProps {
  profileData: StudentExtendedProfileData;
  editData: Partial<StudentExtendedProfileData>;
  activeSectionEdit: string | null;
  setActiveSectionEdit: (section: string | null) => void;
  updateEditData: (field: string, value: any) => void;
  updateProfile: (data: Partial<StudentExtendedProfileData>) => void;
  getLearningGoalText: (goal: string) => string;
  isModified?: boolean;
}

// Fetch subjects from Supabase
const fetchSubjects = async (): Promise<ChipOption[]> => {
  const { data, error } = await supabase
    .from('subjects')
    .select('name');
  if (error) throw error;
  return (data || []).map((row: { name: string }) => row.name);
};

const GeneralInformationSection: React.FC<GeneralInformationSectionProps> = ({
  profileData,
  editData,
  activeSectionEdit,
  setActiveSectionEdit,
  updateEditData,
  updateProfile,
  getLearningGoalText,
  isModified = false
}) => {
  // React Query for subjects
  const { data: subjectOptions = [], isLoading: isSubjectsLoading } = useQuery({
    queryKey: ['subjects-list'],
    queryFn: fetchSubjects,
    staleTime: 1000 * 60 * 60 * 12, // 12 hours
  });

  return (
    <>
      {/* General Information Section */}
      <Card>
        <SectionHeader
          title="General Information"
          isModified={isModified}
          onEdit={() => setActiveSectionEdit('general')}
        />
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h3 className="text-sm font-medium text-gray-500">Subjects:</h3>
              <div className="flex flex-wrap gap-2 mt-1">
                {profileData.subjectsOfInterest.length > 0 ? (
                  profileData.subjectsOfInterest.map((subject, index) => (
                    <Badge key={index} variant="secondary">
                      {subject}
                    </Badge>
                  ))
                ) : (
                  <p className="text-gray-500">No subjects specified</p>
                )}
              </div>
            </div>

            <div>
              <h3 className="text-sm font-medium text-gray-500">Learning Goals:</h3>
              <ul className="list-disc pl-5 space-y-1 mt-1">
                {profileData.learningGoals.length > 0 ? (
                  profileData.learningGoals.map((goalId, index) => {
                    const goalObj = learningGoalsList.find(g => g.id === goalId);
                    return (
                      <li key={index} className="text-sm">{goalObj ? goalObj.label : goalId}</li>
                    );
                  })
                ) : (
                  <p className="text-gray-500">No learning goals specified</p>
                )}
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* General Information Edit Modal */}
      {activeSectionEdit === 'general' && (
        <Modal
          isOpen={activeSectionEdit === 'general'}
          onClose={() => setActiveSectionEdit(null)}
          title="Edit General Information"
        >
          <div className="space-y-5">
            <ChipInput
              label="Subjects"
              value={editData.subjectsOfInterest || []}
              onChange={(chips) => {
                // Always store as array of strings
                const values = Array.isArray(chips)
                  ? chips.map((chip) => typeof chip === 'string' ? chip : (chip.label || chip.value))
                  : [];
                updateEditData("subjectsOfInterest", values);
              }}
              options={subjectOptions}
              allowCustom={false}
              multi={true}
              placeholder="Select subjects"
              isLoading={isSubjectsLoading}
              required
            />
            <ChipInput
              label="Learning Goals"
              value={editData.learningGoals || []}
              onChange={(chips) => {
                // Always store as array of IDs (strings)
                const values = Array.isArray(chips)
                  ? chips.map((chip) => typeof chip === 'string' ? chip : chip.id)
                  : [];
                updateEditData("learningGoals", values);
              }}
              options={learningGoalsList}
              getOptionLabel={opt => typeof opt === 'string' ? opt : opt.label}
              getOptionValue={opt => typeof opt === 'string' ? opt : opt.value}
              allowCustom={false}
              multi={true}
              placeholder="Select learning goals"
              required
            />
          </div>
          <div className="flex flex-row w-full gap-2 mt-6 justify-between">
            <Button variant="outline" onClick={() => setActiveSectionEdit(null)} className="w-32">
              Cancel
            </Button>
            <Button onClick={() => {
              const changes = {
                subjectsOfInterest: editData.subjectsOfInterest,
                learningGoals: editData.learningGoals
              };
              updateProfile(changes);
              setActiveSectionEdit(null);
            }} className="w-32">
              OK
            </Button>
          </div>
        </Modal>
      )}
    </>
  );
};

export default GeneralInformationSection;
